import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { BrowserRouter, Link, Route, Switch } from "react-router-dom";
import { ProductType } from "./api/model/common";
import { ProcessingDialog } from "./components/dialogs/ProcessingDialog";
import { ExternalRedirect } from "./components/ExternalRedirect";
import config, { Routes } from "./config";
import ProcessingContextProvider from "./contexts/ProcessingContext";
import { MainLayout } from "./layout/MainLayout";
import { ContractCheckForm } from "./pages/ContractCheckForm";
import { MessagePage } from "./pages/MessagePage";
import { NoProductMessage } from "./pages/MessagePage/Message/NoProductMessage";
import { ReglamentMessage } from "./pages/MessagePage/Message/ReglamentMessage";
import { QuotationForm } from "./pages/QuotationForm";
import { SuccessfulQuotationComponent } from "./pages/SuccessfulQuotation";
import { QuotationTravelForm } from "./pages/TravelQuotationForm";
import { UnsuccessfulPaymentComponent } from "./pages/UnsuccessfulPayment";
import { strings } from "./strings/strings";
import { useHotjar } from "./utils/hooks/useHotjar";
import { updateUTMFromUrl } from "./utils/utm";

export const WidgetTooltip = window["WidgetTooltip"];
export const WIDGET_DURATION = 10000;

const isProduction = process.env.NODE_ENV === "production";

const App = () => {
    strings.setLanguage("ua");
    const { initializeHotjar } = useHotjar();

    useEffect(() => {
        initializeHotjar();
        updateUTMFromUrl();
    }, []);

    return (
        <ProcessingContextProvider>
            <ProcessingDialog />
            <BrowserRouter basename={"/store"}>
                <Switch>
                    <Route exact path={["/"]}>
                        <MainLayout homeUrl={`${config.ARX_URL}`} type={ProductType.TRAVEL}>
                            {isProduction ? (
                                <ExternalRedirect href={config.ARX_URL} />
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column" }}>
                                    <Link to={"/travel"}>{"travel"}</Link>
                                    <Link to={"/green-card"}>{"green-card"}</Link>
                                </div>
                            )}
                        </MainLayout>
                    </Route>
                    <Route exact path={["/check"]}>
                        <Helmet>
                            <title>{strings.head.check.title}</title>
                            <meta
                                name={"description"}
                                content={`${strings.head.check.metaDescription}`}
                            />
                        </Helmet>
                        <MainLayout homeUrl={`${config.ARX_URL}`} type={ProductType.TRAVEL}>
                            <ContractCheckForm />
                        </MainLayout>
                    </Route>
                    <Route exact path={["/travel"]}>
                        <Helmet>
                            <title>{strings.head.travel.title}</title>
                            <meta
                                name={"description"}
                                content={`${strings.head.travel.metaDescription}`}
                            />
                        </Helmet>

                        <MainLayout homeUrl={`${config.ARX_URL}`} type={ProductType.TRAVEL}>
                            <QuotationTravelForm
                                salesVariant={"svs_02110034_simple"}
                                scenarioCode={"travelWeb"}
                            />
                        </MainLayout>
                    </Route>
                    <Route path={"/travel/quotation/successful/:quotationId"}>
                        <Helmet>
                            <title>{strings.head.travel.title}</title>
                            <meta
                                name={"description"}
                                content={`${strings.head.travel.metaDescription}`}
                            />
                        </Helmet>

                        <SuccessfulQuotationComponent
                            homeUrl={config.ARX_URL}
                            type={ProductType.TRAVEL}
                        />
                    </Route>
                    <Route path={"/travel/payment/unsuccessful/:quotationId"}>
                        <Helmet>
                            <title>{strings.head.travel.title}</title>
                            <meta
                                name={"description"}
                                content={`${strings.head.travel.metaDescription}`}
                            />
                        </Helmet>

                        <UnsuccessfulPaymentComponent
                            homeUrl={config.ARX_URL}
                            type={ProductType.TRAVEL}
                        />
                    </Route>

                    <Route path={["/emtpl", "/emtpl-smart-drive"]}>
                        <ExternalRedirect href={"/shop/emtpl"} />
                    </Route>

                    <Route exact path={[Routes.greenQuotationRoot]}>
                        <Helmet>
                            <title>{strings.head.greenCard.title}</title>
                            <meta
                                name={"description"}
                                content={`${strings.head.greenCard.metaDescription}`}
                            />
                        </Helmet>

                        <MainLayout homeUrl={`${config.ARX_URL}`} type={ProductType.GREEN_CARD}>
                            <QuotationForm
                                salesVariant={"svs_09710001_simple"}
                                scenarioCode={"greenCardPublic"}
                                type={ProductType.GREEN_CARD}
                            />
                        </MainLayout>
                    </Route>
                    <Route path={Routes.greenSuccessfulQuotationPath + "/:quotationId"}>
                        <Helmet>
                            <title>{strings.head.greenCard.title}</title>
                            <meta
                                name={"description"}
                                content={`${strings.head.greenCard.metaDescription}`}
                            />
                        </Helmet>

                        <SuccessfulQuotationComponent
                            homeUrl={config.ARX_URL}
                            type={ProductType.GREEN_CARD}
                        />
                    </Route>
                    <Route path={Routes.greenUnsuccessfulQuotationPath + "/:quotationId"}>
                        <Helmet>
                            <title>{strings.head.greenCard.title}</title>
                            <meta
                                name={"description"}
                                content={`${strings.head.greenCard.metaDescription}`}
                            />
                        </Helmet>

                        <UnsuccessfulPaymentComponent
                            homeUrl={config.ARX_URL}
                            type={ProductType.GREEN_CARD}
                        />
                    </Route>
                    <Route path={"/technical"}>
                        <Helmet>
                            <title>{strings.head.emtpl.title}</title>
                            <meta
                                name={"description"}
                                content={`${strings.head.emtpl.metaDescription}`}
                            />
                        </Helmet>

                        <MessagePage homeUrl={config.ARX_URL} type={ProductType.EMTPL}>
                            <ReglamentMessage
                                homeUrl={config.ARX_URL}
                                errorMessage1={strings.reglamentErrorMessage1}
                                errorMessage2={strings.reglamentErrorMessage2}
                            />
                        </MessagePage>
                    </Route>
                    <Route path={"/technical-gree-card"}>
                        <Helmet>
                            <title>{strings.head.emtpl.title}</title>
                            <meta
                                name={"description"}
                                content={`${strings.head.emtpl.metaDescription}`}
                            />
                        </Helmet>

                        <MessagePage homeUrl={config.ARX_URL} type={ProductType.GREEN_CARD}>
                            <ReglamentMessage
                                homeUrl={config.ARX_URL}
                                errorMessage1={strings.reglamentErrorMessage1}
                                errorMessage2={strings.reglamentErrorMessage2_GreenCard}
                            />
                        </MessagePage>
                    </Route>
                    <Route path={"/no-product"}>
                        <Helmet>
                            <title>{strings.head.emtpl.title}</title>
                            <meta
                                name={"description"}
                                content={`${strings.head.emtpl.metaDescription}`}
                            />
                        </Helmet>

                        <MessagePage homeUrl={config.ARX_URL} type={ProductType.EMTPL}>
                            <NoProductMessage homeUrl={config.ARX_URL} />
                        </MessagePage>
                    </Route>
                </Switch>
            </BrowserRouter>
        </ProcessingContextProvider>
    );
};

export default App;
